import { confirmDialog, reportLoadError, notify } from '../utils/notify';
// Imports al inicio del archivo
import React, { useState, useEffect, useContext } from 'react';
import { payrollAPI, employeeAPI, contractsAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import PayrollCalculator from '../components/PayrollCalculator';
import {
  Briefcase, Calculator, CalendarDays, Check, Download, Eye, FileText, Landmark, ListOrdered, Plus, Receipt, ScrollText,
  Trash2, TriangleAlert, User, Wallet, X,
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { generatePayslipPDF } from '../utils/generatePayslipPDF';
import { exportPayrollToExcel } from '../utils/excelExport';
import { toLocalISODate, parseLocalDate, toDateInputValue } from '../utils/dateUtils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// ← NO importes nada de date-fns/locale ni registerLocale
import './Payroll.css';
const Payroll = () => {
  const { user } = useContext(AuthContext);
  const { t, locale, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('calculate');
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // PayrollCalculator states
  const [showCalculator, setShowCalculator] = useState(false);
  
  // Detail modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailPayroll, setDetailPayroll] = useState(null);
  
  const [calculateForm, setCalculateForm] = useState({
    period_start: '',
    period_end: '',
    employee_id: ''
  });
  const [recordsFilter, setRecordsFilter] = useState({
    start: '',
    end: ''
  });
  useEffect(() => {
    if (activeTab === 'records' && !recordsFilter.start && !recordsFilter.end) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
      setRecordsFilter({
        start: toLocalISODate(firstDay),
        end: toLocalISODate(lastDay)
      });
    }
  }, [activeTab]);

  const [approveForm, setApproveForm] = useState({
    payment_method: 'bank_transfer',
    payment_date: toLocalISODate(new Date()),
    notes: ''
  });

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    
    loadEmployees();
    loadSummary();
    loadContracts();
    if (activeTab === 'records') {
      loadPayrollRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ⭐ Default al mes actual al montar el componente
useEffect(() => {
  if (!calculateForm.period_start && !calculateForm.period_end) {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Último día del mes

    setCalculateForm({
      ...calculateForm,
      period_start: toLocalISODate(firstDay),
      period_end: toLocalISODate(lastDay)
    });
  }
}, []); // [] = solo al montar

  const loadEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data.employees.filter(e => e.status === 'active'));
    } catch (error) {
      console.error('Error loading employees:', error);
      reportLoadError(error, t('Error loading employees'), { main: true });
    }
  };

  const loadContracts = async () => {
    try {
      const response = await contractsAPI.getAll({ status: '' });
      setContracts(response.data.contracts);
    } catch (error) {
      console.error('Error loading contracts:', error);
      reportLoadError(error, t('Error loading contracts'));
    }
  };

  const loadSummary = async () => {
    try {
      const start = calculateForm.period_start || toLocalISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
      const end = calculateForm.period_end || toLocalISODate(new Date());
      
      const summaryRes = await payrollAPI.getSummary({
        period_start: start,
        period_end: end
      });
      setSummary(summaryRes.data.summary);
    } catch (error) {
      console.error('Error loading summary:', error);
      reportLoadError(error, t('Error loading payroll summary'));
    }
  };
  const loadPayrollRecords = async () => {
    try {
      setLoading(true);
  
      const start = recordsFilter.start || 
        toLocalISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
      
      const end = recordsFilter.end || 
        toLocalISODate(new Date());
  
      const response = await payrollAPI.getAll({
        period_start: start,
        period_end: end
      });
  
  
      setPayrollRecords(response.data.payroll || []);
    } catch (error) {
      console.error('Error loading payroll records:', error);
      setPayrollRecords([]);
      reportLoadError(error, t('Error cargando records. Revisa consola.'), { main: true });
    } finally {
      setLoading(false);
    }
  };
  const handleCalculate = async (e) => {
    e.preventDefault();
    
    if (!calculateForm.period_start || !calculateForm.period_end) {
      notify(t('Please select period start and end dates'), 'warning');
      return;
    }
    // ⭐ VALIDACIÓN: end >= start
    if (new Date(calculateForm.period_end) < new Date(calculateForm.period_start)) {
    notify(t('End date must be on or after start date'), 'warning');
    return;
    }

    try {
      setLoading(true);
      const response = await payrollAPI.calculate(calculateForm);
      
      if (response.data.success) {
        notify(`✅ ${response.data.message}\n\n${t('Records created: {count}', { count: response.data.payroll.length })}`, 'success');
        
        if (response.data.errors && response.data.errors.length > 0) {
          const errorMsg = response.data.errors.map(e => 
            `${e.employee}: ${e.error}`
          ).join('\n');
          notify(`${t('Some issues:')}\n\n${errorMsg}`, 'error');
        }
        // Saved, but worth a look before approving (negative net pay, a
        // deduction that could not be taken).
        if (response.data.warnings && response.data.warnings.length > 0) {
          notify(`${t('Check before approving:')}\n\n${response.data.warnings.map(w => `${w.employee}: ${w.message}`).join('\n')}`, 'warning');
        }
        // ⭐ UPDATE OPTIMISTA: mostramos los calculados inmediatamente
  setPayrollRecords(response.data.payroll || [])
        
        await loadSummary();
        await loadPayrollRecords();
        setActiveTab('records');
      }
    } catch (error) {
      console.error('Error calculating payroll:', error);
      notify(error.response?.data?.message || t('Error calculating payroll'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Start from what the payroll already has (its payment date, or the end of
  // the period, which is what calculation stores), not from today.
  const openApproveModal = (record) => {
    setSelectedPayroll(record);
    setApproveForm({
      payment_method: ['bank_transfer', 'check', 'cash'].includes(record.payment_method) ? record.payment_method : 'bank_transfer',
      payment_date: toDateInputValue(record.payment_date || record.period_end),
      notes: ''
    });
    setShowApproveModal(true);
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      await payrollAPI.approve(selectedPayroll.id, approveForm);
      notify(t('Payroll approved successfully!'), 'success');
      setShowApproveModal(false);
      setSelectedPayroll(null);
      setApproveForm({
        payment_method: 'bank_transfer',
        payment_date: toLocalISODate(new Date()),
        notes: ''
      });
      await loadPayrollRecords();
      await loadSummary();
    } catch (error) {
      console.error('Error approving payroll:', error);
      notify(error.response?.data?.message || t('Error approving payroll'), 'error');
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) {
      notify(t('Please provide a reason for rejection'), 'warning');
      return;
    }

    try {
      await payrollAPI.reject(selectedPayroll.id, { notes: rejectReason });
      notify(t('Payroll rejected'), 'success');
      setShowRejectModal(false);
      setSelectedPayroll(null);
      setRejectReason('');
      await loadPayrollRecords();
      await loadSummary();
    } catch (error) {
      console.error('Error rejecting payroll:', error);
      notify(error.response?.data?.message || t('Error rejecting payroll'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog(t('Are you sure you want to delete this payroll record?'))) {
      return;
    }

    try {
      await payrollAPI.delete(id);
      notify(t('Payroll record deleted'), 'success');
      await loadPayrollRecords();
      await loadSummary();
    } catch (error) {
      console.error('Error deleting payroll:', error);
      notify(error.response?.data?.message || t('Error deleting payroll'), 'error');
    }
  };

  const handleViewDetail = async (record) => {
    setDetailPayroll(record);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: t('Pending'),
      approved: t('Approved'),
      rejected: t('Rejected')
    };
    const known = badges[status] ? status : 'pending';

    return <StatusBadge status={known}>{badges[known]}</StatusBadge>;
  };

  // The summary's money, one line per currency (never SRD + USD).
  const summaryMoney = (field) => (summary?.by_currency?.length ? summary.by_currency : [{ currency: 'SRD', [field]: 0 }])
    .map(c => (
      <span key={c.currency} className="pay-currency-line">{formatCurrency(c[field], c.currency)}</span>
    ));

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const handleExportExcel = () => {
    if (payrollRecords.length === 0) {
      notify(t('No data to export'), 'warning');
      return;
    }
    const success = exportPayrollToExcel(payrollRecords, language);
    if (success) {
      notify(t('File exported'), 'success');
    } else {
      notify(t('Error exporting the file'), 'error');
    }
  };

   const getEmployeeContract = (employeeId) => {
  const contract = contracts.find(c => {
    const match = c.employee_id === employeeId;
    const statusValid = ['active', 'signed'].includes(c.status);

    return match && statusValid;
  });
  
  return contract;
};
  const canApprove = ['super_admin', 'finance_manager'].includes(user?.role);
  const canDelete = user?.role === 'super_admin';

  return (
    <div className="payroll-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Payroll Management')}</h1>
          <p className="page-sub">{t('Calculate and manage employee payments')}</p>
        </div>
        
        <div className="page-actions">
          {activeTab === 'records' && payrollRecords.length > 0 && (
            <button onClick={handleExportExcel} className="btn btn--secondary">
              <Download aria-hidden="true" /> {t('Export Excel')}
            </button>
          )}
          <button 
            onClick={() => {
              if (employees.length === 0) {
                notify(t('Please add employees first'), 'warning');
                return;
              }
              setActiveTab('individual');
            }}
            className="btn btn--primary"
          >
            <Plus aria-hidden="true" /> {t('Create Individual Payroll')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="kpi-grid">
          <div className="kpi">
            <h3 className="kpi__label">{t('Total Records')}</h3>
            <p className="kpi__value">{summary.total_records || 0}</p>
            <p className="kpi__foot">{t('{count} employees', { count: summary.total_employees || 0 })}</p>
          </div>
          <div className="kpi">
            <h3 className="kpi__label">{t('Total Gross Pay')}</h3>
            <p className="kpi__value">{summaryMoney('total_gross')}</p>
          </div>
          <div className="kpi">
            <h3 className="kpi__label">{t('Total Net Pay')}</h3>
            <p className="kpi__value">{summaryMoney('total_net')}</p>
          </div>
          <div className="kpi">
            <h3 className="kpi__label">{t('Total Taxes')}</h3>
            <p className="kpi__value">{summaryMoney('total_tax')}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabbar">
        <button
          className={`tabbar__tab${activeTab === 'calculate' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('calculate')}
        >
          <Calculator aria-hidden="true" /> {t('Bulk Calculate')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'individual' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('individual')}
        >
          <User aria-hidden="true" /> {t('Individual Payroll')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'records' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('records')}
        >
          <ListOrdered aria-hidden="true" /> {t('Payroll Records')}
        </button>
      </div>

      {/* Bulk Calculate Tab */}
      {activeTab === 'calculate' && (
        <div className="pay-content">
          <div className="card pay-panel">
            <h2 className="card__title"><Calculator aria-hidden="true" /> {t('Bulk Payroll Calculation')}</h2>
            <p className="pay-lead">{t('Calculate payroll for multiple employees at once for a specific period.')}</p>

            <form onSubmit={handleCalculate} className="pay-calculate-form">
  <div className="pay-form-row">
    <div className="pay-field">
      <label className="field-label">{t('Period Start Date *')}</label>
      <DatePicker
        selected={calculateForm.period_start ? parseLocalDate(calculateForm.period_start) : null}
        onChange={(date) => {
          const formatted = date ? toLocalISODate(date) : '';
          setCalculateForm({ ...calculateForm, period_start: formatted });
        }}
        selectsStart
        startDate={calculateForm.period_start ? parseLocalDate(calculateForm.period_start) : null}
        endDate={calculateForm.period_end ? parseLocalDate(calculateForm.period_end) : null}
        maxDate={calculateForm.period_end ? parseLocalDate(calculateForm.period_end) : null}
        dateFormat={locale === 'nl-NL' ? 'dd-MM-yyyy' : 'MM/dd/yyyy'}
        placeholderText={t('Select start date')}
        className="input"
        wrapperClassName="date-picker-wrapper"
        portalId="root"
        required
      />
    </div>

    <div className="pay-field">
      <label className="field-label">{t('Period End Date *')}</label>
      <DatePicker
        selected={calculateForm.period_end ? parseLocalDate(calculateForm.period_end) : null}
        onChange={(date) => {
          const formatted = date ? toLocalISODate(date) : '';
          setCalculateForm({ ...calculateForm, period_end: formatted });
        }}
        selectsEnd
        startDate={calculateForm.period_start ? parseLocalDate(calculateForm.period_start) : null}
        endDate={calculateForm.period_end ? parseLocalDate(calculateForm.period_end) : null}
        minDate={calculateForm.period_start ? parseLocalDate(calculateForm.period_start) : null}
        dateFormat={locale === 'nl-NL' ? 'dd-MM-yyyy' : 'MM/dd/yyyy'}
        placeholderText={t('Select end date')}
        className="input"
        wrapperClassName="date-picker-wrapper"
        portalId="root"
        required
      />
    </div>
  </div>

  <div className="pay-field">
    <label className="field-label">{t('Employee (Optional)')}</label>
    <select className="select"
      value={calculateForm.employee_id}
      onChange={(e) => setCalculateForm({ ...calculateForm, employee_id: e.target.value })}
    >
      <option value="">{t('All Active Employees')}</option>
      {employees.map(emp => (
        <option key={emp.id} value={emp.id}>
          {emp.first_name} {emp.last_name} - {emp.position}
        </option>
      ))}
    </select>
    <small className="field-hint">{t('Leave empty to calculate for all active employees')}</small>
  </div>

  <button type="submit" className="btn btn--primary" disabled={loading}>
    {loading ? t('Calculating...') : <><Calculator aria-hidden="true" /> {t('Calculate Payroll')}</>}
  </button>
</form>
          </div>
        </div>
      )}

      {/* Individual Payroll Tab */}
      {activeTab === 'individual' && (
        <div className="pay-content">
          <div className="card pay-panel">
            <h2 className="card__title"><User aria-hidden="true" /> {t('Manual Payroll Calculator')}</h2>
            <p className="pay-lead">{t('Calculate payroll for one or multiple employees with full control over taxes and deductions.')}</p>

            <button 
              onClick={() => setShowCalculator(true)}
              className="btn btn--primary"
              disabled={employees.length === 0}
            >
              <Calculator aria-hidden="true" /> {t('Open Payroll Calculator')}
            </button>

            {employees.length === 0 && (
              <p className="pay-warning"><TriangleAlert aria-hidden="true" /> {t('No active employees found. Please add employees first.')}</p>
            )}
          </div>
        </div>
      )}

      {/* Records Tab */}
      {activeTab === 'records' && (
  <div className="pay-content">
    {/* FILTRO DE FECHAS (siempre visible arriba) */}
    <div className="card pay-panel pay-filter">
      <h3 className="card__title"><CalendarDays aria-hidden="true" /> {t('Filter Payroll Records by Period')}</h3>
      <div className="pay-form-row">
        <div className="pay-field">
          <label className="field-label">{t('Start Date')}</label>
          <DatePicker
            selected={recordsFilter.start ? parseLocalDate(recordsFilter.start) : null}
            onChange={(date) => {
              const formatted = date ? toLocalISODate(date) : '';
              setRecordsFilter({ ...recordsFilter, start: formatted });
            }}
            selectsStart
            startDate={recordsFilter.start ? parseLocalDate(recordsFilter.start) : null}
            endDate={recordsFilter.end ? parseLocalDate(recordsFilter.end) : null}
            maxDate={recordsFilter.end ? parseLocalDate(recordsFilter.end) : null}
            dateFormat={locale === 'nl-NL' ? 'dd-MM-yyyy' : 'MM/dd/yyyy'}
            placeholderText={t('Select start date')}
            className="input"
            wrapperClassName="date-picker-wrapper"
            portalId="root" // ← FIX
          />
        </div>

        <div className="pay-field">
          <label className="field-label">{t('End Date')}</label>
          <DatePicker
            selected={recordsFilter.end ? parseLocalDate(recordsFilter.end) : null}
            onChange={(date) => {
              const formatted = date ? toLocalISODate(date) : '';
              setRecordsFilter({ ...recordsFilter, end: formatted });
            }}
            selectsEnd
            startDate={recordsFilter.start ? parseLocalDate(recordsFilter.start) : null}
            endDate={recordsFilter.end ? parseLocalDate(recordsFilter.end) : null}
            minDate={recordsFilter.start ? parseLocalDate(recordsFilter.start) : null}
            dateFormat={locale === 'nl-NL' ? 'dd-MM-yyyy' : 'MM/dd/yyyy'}
            placeholderText={t('Select end date')}
            className="input"
            wrapperClassName="date-picker-wrapper"
            portalId="root" // ← FIX
          />
        </div>

        <div className="pay-field pay-field--action">
          <button
            onClick={loadPayrollRecords}
            className="btn btn--primary"
          >
            {t('Apply Filter')}
          </button>
        </div>
      </div>
    </div>

    {/* CONTENIDO DE LA TAB (loading, empty, tabla) */}
    {loading ? (
      <div className="empty">{t('Loading payroll records...')}</div>
    ) : payrollRecords.length === 0 ? (
      <div className="empty">
        <p>{t('No payroll records found for the selected period.')}</p>
        <p>{t('Try changing the date range or calculate new payrolls.')}</p>
      </div>
    ) : (
      <div className="pay-table-wrap">
        <table className="data-table pay-table">
          <thead>
            <tr>
              <th>{t('Employee')}</th>
              <th>{t('Period')}</th>
              <th>{t('Type')}</th>
              <th>{t('Days/Hours')}</th>
              <th className="num">{t('Gross Pay')}</th>
              <th className="num">{t('Taxes')}</th>
              <th className="num">{t('Net Pay')}</th>
              <th>{t('Status')}</th>
              <th>{t('Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {payrollRecords.map(record => {
              const contract = getEmployeeContract(record.employee_id);
              
              return (
                <tr key={record.id}>
                  <td>
                    <strong>{record.first_name} {record.last_name}</strong>
                    <br />
                    <small className="field-hint">{record.position}</small>
                    {contract && (
                      <div className="pay-contract">
                        <ScrollText aria-hidden="true" /> {contract.contract_type}
                        {contract.end_date && (
                          <span className="pay-contract__until">
                            {t('Until')} {parseLocalDate(contract.end_date)?.toLocaleDateString(locale)}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td data-label={t('Period')}>
                    {parseLocalDate(record.period_start)?.toLocaleDateString(locale)} -  
                    <br />
                    {parseLocalDate(record.period_end)?.toLocaleDateString(locale)}
                  </td>
                  <td data-label={t('Type')}>
                    <StatusBadge variant="neutral">
                      {record.payment_type === 'hourly' ? t('Hourly') : t('Monthly')}
                    </StatusBadge>
                  </td>
                  <td data-label={t('Days/Hours')} className="num">
                    {record.payment_type === 'hourly' ? (
                      <>
                        <strong>{parseFloat(record.hours_worked || record.regular_hours || 0).toFixed(1)}h</strong>
                        <br />
                        <small className="field-hint">{t('OT')}: {parseFloat(record.overtime_hours || 0).toFixed(1)}h</small>
                      </>
                    ) : (
                      <>{record.days_worked || '-'} {t('days')}</>
                    )}
                  </td>
                  <td data-label={t('Gross Pay')} className="num"><strong>{formatCurrency(record.gross_pay, record.currency)}</strong></td>
                  <td data-label={t('Taxes')} className="num">{formatCurrency(record.tax_amount, record.currency)}</td>
                  <td data-label={t('Net Pay')} className="num"><strong className="pay-net">{formatCurrency(record.net_pay, record.currency)}</strong></td>
                  <td data-label={t('Status')}>{getStatusBadge(record.status)}</td>
                  <td>
                    <div className="pay-actions">
                      <button type="button" onClick={() => handleViewDetail(record)} className="btn btn--secondary btn--icon" title={t('View Details')} aria-label={t('View Details')}><Eye aria-hidden="true" /></button>
                      <button
                        onClick={() => {
                          const employee = employees.find(e => e.id === record.employee_id);
                          const company = { name: 'Hardwall Security', address: 'Paramaribo, Suriname', email: 'info@hardwall.com', phone: '+597 123-4567' };
                          generatePayslipPDF(record, employee, company, language);
                        }}
                        type="button"
                        className="btn btn--secondary btn--icon"
                        title={t('Download PDF')}
                        aria-label={t('Download PDF')}
                      >
                        <FileText aria-hidden="true" />
                      </button>
                      {record.status === 'pending' && canApprove && (
                        <>
                          <button onClick={() => openApproveModal(record)} className="btn btn--secondary btn--icon pay-approve" title={t('Approve')} aria-label={t('Approve')}><Check aria-hidden="true" /></button>
                          <button onClick={() => { setSelectedPayroll(record); setShowRejectModal(true); }} className="btn btn--secondary btn--icon btn--danger-text pay-reject" title={t('Reject')} aria-label={t('Reject')}><X aria-hidden="true" /></button>
                        </>
                      )}
                      {canDelete && ['pending', 'rejected'].includes(record.status) && !record.consolidation_id && (
                        <button onClick={() => handleDelete(record.id)} className="btn btn--secondary btn--icon btn--danger-text pay-delete" title={t('Delete')} aria-label={t('Delete')}><Trash2 aria-hidden="true" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

      {/* Approve Modal */}
      {showApproveModal && selectedPayroll && (
        <Modal open title={t('Approve Payroll')} onClose={() => setShowApproveModal(false)} size="md">

            <div className="pay-dialog">
              <div className="pay-info">
                <h3 className="pay-section__title">{t('Payroll Details')}</h3>
                <p><strong>{t('Employee:')}</strong> {selectedPayroll.first_name} {selectedPayroll.last_name}</p>
                <p><strong>{t('Period:')}</strong> {parseLocalDate(selectedPayroll.period_start)?.toLocaleDateString(locale)} - {parseLocalDate(selectedPayroll.period_end)?.toLocaleDateString(locale)}</p>
                <p><strong>{t('Net Pay:')}</strong> {formatCurrency(selectedPayroll.net_pay, selectedPayroll.currency)}</p>
              </div>

              <form onSubmit={handleApprove} className="pay-approve-form">
                <div className="pay-field">
                  <label className="field-label">{t('Payment Method *')}</label>
                  <select className="select"
                    value={approveForm.payment_method}
                    onChange={(e) => setApproveForm({...approveForm, payment_method: e.target.value})}
                    required
                  >
                    <option value="bank_transfer">{t('Bank Transfer')}</option>
                    <option value="check">{t('Check')}</option>
                    <option value="cash">{t('Cash')}</option>
                  </select>
                </div>

                <div className="pay-field">
                  <label className="field-label">{t('Payment Date *')}</label>
                  <input className="input"
                    type="date"
                    value={approveForm.payment_date}
                    onChange={(e) => setApproveForm({...approveForm, payment_date: e.target.value})}
                    required
                  />
                </div>

                <div className="pay-field">
                  <label className="field-label">{t('Notes')}</label>
                  <textarea className="textarea"
                    value={approveForm.notes}
                    onChange={(e) => setApproveForm({...approveForm, notes: e.target.value})}
                    rows="3"
                    placeholder={t('Optional notes...')}
                  />
                </div>

                <div className="pay-form-actions">
                  <button type="button" onClick={() => setShowApproveModal(false)} className="btn btn--secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary">
                    <Check aria-hidden="true" /> {t('Approve Payment')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPayroll && (
        <Modal open title={t('Reject Payroll')} onClose={() => setShowRejectModal(false)} size="md">

            <div className="pay-dialog">
              <p>{t('Employee:')} <strong>{selectedPayroll.first_name} {selectedPayroll.last_name}</strong></p>

              <form onSubmit={handleReject}>
                <div className="pay-field">
                  <label className="field-label">{t('Reason for Rejection *')}</label>
                  <textarea className="textarea"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows="4"
                    placeholder={t('Please provide a reason...')}
                    required
                  />
                </div>

                <div className="pay-form-actions">
                  <button type="button" onClick={() => setShowRejectModal(false)} className="btn btn--secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--danger">
                    <X aria-hidden="true" /> {t('Reject Payroll')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}

      {/* PayrollCalculator Modal */}
      {showCalculator && (
        <PayrollCalculator
          employees={employees}
          onClose={() => {
            setShowCalculator(false);
          }}
          onSave={() => {
            loadPayrollRecords();
            loadSummary();
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && detailPayroll && (
        <Modal open title={t('Payroll Details')} onClose={() => setShowDetailModal(false)} size="lg">

            <div className="pay-dialog pay-detail">
              {/* Employee Info */}
              <div className="pay-section">
                <h3 className="pay-section__title"><User aria-hidden="true" /> {t('Employee Information')}</h3>
                <div className="pay-detail-grid">
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Name:')}</span>
                    <span className="pay-detail-value">{detailPayroll.first_name} {detailPayroll.last_name}</span>
                  </div>
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Position:')}</span>
                    <span className="pay-detail-value">{detailPayroll.position}</span>
                  </div>
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Department:')}</span>
                    <span className="pay-detail-value">{detailPayroll.department}</span>
                  </div>
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Payment Type:')}</span>
                    <span className="pay-detail-value">{t(detailPayroll.payment_type)}</span>
                  </div>
                </div>
              </div>

              {/* Period Info */}
              <div className="pay-section">
                <h3 className="pay-section__title"><CalendarDays aria-hidden="true" /> {t('Pay Period')}</h3>
                <div className="pay-detail-grid">
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Period:')}</span>
                    <span className="pay-detail-value">
                      {parseLocalDate(detailPayroll.period_start)?.toLocaleDateString(locale)} - {parseLocalDate(detailPayroll.period_end)?.toLocaleDateString(locale)}
                    </span>
                  </div>
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Payment Date:')}</span>
                    <span className="pay-detail-value">{parseLocalDate(detailPayroll.payment_date || detailPayroll.period_end)?.toLocaleDateString(locale)}</span>
                  </div>
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Status:')}</span>
                    <span className="pay-detail-value">{getStatusBadge(detailPayroll.status)}</span>
                  </div>
                  <div className="pay-detail-item">
                    <span className="pay-detail-label">{t('Currency:')}</span>
                    <span className="pay-detail-value">{detailPayroll.currency}</span>
                  </div>
                </div>
              </div>

              {/* Work Details */}
              <div className="pay-section">
                <h3 className="pay-section__title"><Briefcase aria-hidden="true" /> {t('Work Details')}</h3>
                <div className="pay-detail-grid">
                  {detailPayroll.payment_type === 'hourly' ? (
                    <>
                      <div className="pay-detail-item">
                        <span className="pay-detail-label">{t('Payment Type:')}</span>
                        <span className="pay-detail-value">{t('Hourly')}</span>
                      </div>
                      <div className="pay-detail-item">
                        <span className="pay-detail-label">{t('Hourly Rate:')}</span>
                        <span className="pay-detail-value">
                          {formatCurrency(detailPayroll.hourly_rate || 0, detailPayroll.currency)}
                        </span>
                      </div>
                      <div className="pay-detail-item">
                        <span className="pay-detail-label">{t('Hours Worked:')}</span>
                        <span className="pay-detail-value">
                          {parseFloat(detailPayroll.hours_worked || detailPayroll.regular_hours || 0).toFixed(1)} {t('hours')}
                        </span>
                      </div>
                      {detailPayroll.overtime_hours && parseFloat(detailPayroll.overtime_hours) > 0 && (
                        <>
                          <div className="pay-detail-item">
                            <span className="pay-detail-label">{t('Overtime Hours:')}</span>
                            <span className="pay-detail-value">{parseFloat(detailPayroll.overtime_hours).toFixed(1)} {t('hours')}</span>
                          </div>
                          <div className="pay-detail-item">
                            <span className="pay-detail-label">{t('Overtime Pay:')}</span>
                            <span className="pay-detail-value">
                              {formatCurrency(detailPayroll.overtime_pay || 0, detailPayroll.currency)}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="pay-detail-item">
                        <span className="pay-detail-label">{t('Payment Type:')}</span>
                        <span className="pay-detail-value">{t('Monthly')}</span>
                      </div>
                      {detailPayroll.days_worked ? (
                        <>
                          <div className="pay-detail-item">
                            <span className="pay-detail-label">{t('Days Worked:')}</span>
                            <span className="pay-detail-value">{detailPayroll.days_worked} {t('days')}</span>
                          </div>
                          <div className="pay-detail-item">
                            <span className="pay-detail-label">{t('Calculation:')}</span>
                            <span className="pay-detail-value">
                              {t('Prorated ({worked}/30 days)', { worked: detailPayroll.days_worked })}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="pay-detail-item">
                          <span className="pay-detail-label">{t('Period:')}</span>
                          <span className="pay-detail-value">{t('Full Month (30 days)')}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Earnings Breakdown */}
              <div className="pay-section">
                <h3 className="pay-section__title"><Wallet aria-hidden="true" /> {t('Earnings')}</h3>
                <div className="pay-breakdown">
                  <div className="pay-breakdown__row">
                    <span>{t('Gross Pay')}</span>
                    <span className="num">{formatCurrency(detailPayroll.gross_pay, detailPayroll.currency)}</span>
                  </div>
                  {detailPayroll.bonuses && parseFloat(detailPayroll.bonuses) > 0 && (
                    <div className="pay-breakdown__row">
                      <span>{t('Bonuses')}</span>
                      <span className="num">{formatCurrency(detailPayroll.bonuses, detailPayroll.currency)}</span>
                    </div>
                  )}
                  {detailPayroll.overtime_pay && parseFloat(detailPayroll.overtime_pay) > 0 && (
                    <div className="pay-breakdown__row">
                      <span>{t('Overtime Pay')}</span>
                      <span className="num">{formatCurrency(detailPayroll.overtime_pay, detailPayroll.currency)}</span>
                    </div>
                  )}
                  <div className="pay-breakdown__row is-total">
                    <span><strong>{t('Total Earnings')}</strong></span>
                    <span className="num"><strong>{formatCurrency(detailPayroll.gross_pay, detailPayroll.currency)}</strong></span>
                  </div>
                </div>
              </div>

              {/* Taxes Breakdown */}
              <div className="pay-section">
                <h3 className="pay-section__title"><Landmark aria-hidden="true" /> {t('Taxes Breakdown')}</h3>
                <div className="pay-breakdown">
                  {(() => {
                    try {
                      let taxDetails = detailPayroll.tax_details;
                      
                      if (typeof taxDetails === 'string') {
                        taxDetails = JSON.parse(taxDetails);
                      }
                      
                      if (taxDetails && typeof taxDetails === 'object' && Object.keys(taxDetails).length > 0) {
                        const taxEntries = Object.entries(taxDetails).filter(([key, value]) => {
                          const amount = typeof value === 'object' ? (value.amount || 0) : value;
                          return parseFloat(amount) > 0;
                        });

                        if (taxEntries.length === 0) {
                          return (
                            <>
                              <div className="pay-breakdown__row">
                                <span>{t('Income Tax')}</span>
                                <span className="num">
                                  {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}
                                </span>
                              </div>
                              <div className="pay-breakdown__row is-total">
                                <span><strong>{t('Total Taxes')}</strong></span>
                                <span className="num pay-minus">
                                  <strong>- {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}</strong>
                                </span>
                              </div>
                            </>
                          );
                        }

                        return (
                          <>
                            {taxEntries.map(([key, value]) => {
                              let taxName = key;
                              let taxAmount = 0;
                              let taxRate = null;

                              if (typeof value === 'object') {
                                taxName = value.name || key.replace(/_/g, ' ').toUpperCase();
                                taxAmount = parseFloat(value.amount || 0);
                                taxRate = value.rate;
                              } else {
                                taxName = key.replace(/_/g, ' ').toUpperCase();
                                taxAmount = parseFloat(value);
                              }

                              return (
                                <div key={key} className="pay-breakdown__row">
                                  <span>
                                    {taxName}
                                    {taxRate && (
                                      <small className="pay-rate">
                                        ({taxRate}%)
                                      </small>
                                    )}
                                  </span>
                                  <span className="num">
                                    {formatCurrency(taxAmount, detailPayroll.currency)}
                                  </span>
                                </div>
                              );
                            })}
                            <div className="pay-breakdown__row is-total">
                              <span><strong>Total Taxes</strong></span>
                              <span className="num pay-minus">
                                <strong>- {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}</strong>
                              </span>
                            </div>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <div className="pay-breakdown__row">
                              <span>Income Tax</span>
                              <span className="num">
                                {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}
                              </span>
                            </div>
                            <div className="pay-breakdown__row is-total">
                              <span><strong>Total Taxes</strong></span>
                              <span className="num pay-minus">
                                <strong>- {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}</strong>
                              </span>
                            </div>
                          </>
                        );
                      }
                    } catch (e) {
                      console.error('Error parsing tax details:', e);
                      return (
                        <>
                          <div className="pay-breakdown__row">
                            <span>Income Tax</span>
                            <span className="num">
                              {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}
                            </span>
                          </div>
                          <div className="pay-breakdown__row is-total">
                            <span><strong>Total Taxes</strong></span>
                            <span className="num pay-minus">
                              <strong>- {formatCurrency(detailPayroll.tax_amount || 0, detailPayroll.currency)}</strong>
                            </span>
                          </div>
                        </>
                      );
                    }
                  })()}
                </div>
              </div>

              {/* Deductions Breakdown */}
              {detailPayroll.deduction_details && (
                <div className="pay-section">
                  <h3 className="pay-section__title"><Receipt aria-hidden="true" /> {t('Deductions')}</h3>
                  <div className="pay-breakdown">
                    {(() => {
                      try {
                        const deductionDetails = typeof detailPayroll.deduction_details === 'string'
                          ? JSON.parse(detailPayroll.deduction_details)
                          : detailPayroll.deduction_details;
                        
                        if (deductionDetails && typeof deductionDetails === 'object' && Object.keys(deductionDetails).length > 0) {
                          return (
                            <>
                              {Object.entries(deductionDetails).map(([key, value]) => {
                                const deductionName = typeof value === 'object' && value.name 
                                  ? value.name 
                                  : key.replace(/_/g, ' ');
                                
                                const deductionAmount = typeof value === 'object' && value.amount 
                                  ? value.amount 
                                  : value;
                                
                                if (!deductionAmount || parseFloat(deductionAmount) === 0) return null;
                                
                                return (
                                  <div key={key} className="pay-breakdown__row">
                                    <span>{deductionName}</span>
                                    <span className="num">
                                      {formatCurrency(deductionAmount, detailPayroll.currency)}
                                    </span>
                                  </div>
                                );
                              })}
                              <div className="pay-breakdown__row is-total">
                                <span><strong>{t('Total Deductions')}</strong></span>
                                <span className="num pay-minus">
                                  <strong>- {formatCurrency(detailPayroll.total_deductions || 0, detailPayroll.currency)}</strong>
                                </span>
                              </div>
                            </>
                          );
                        }
                      } catch (e) {
                        console.error('Error parsing deduction details:', e);
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}

              {/* Net Pay */}
              <div className="pay-section">
                <div className="pay-net-box">
                  <span className="pay-net-box__label">{t('NET PAY')}</span>
                  <span className="pay-net-box__amount">{formatCurrency(detailPayroll.net_pay, detailPayroll.currency)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="pay-form-actions">
                <button 
                  onClick={() => setShowDetailModal(false)}
                  className="btn btn--secondary"
                >
                  {t('Close')}
                </button>
                <button
                  onClick={() => {
                    const employee = employees.find(e => e.id === detailPayroll.employee_id);
                    const company = {
                      name: 'Hardwall Security',
                      address: 'Paramaribo, Suriname',
                      email: 'info@hardwall.com',
                      phone: '+597 123-4567'
                    };
                    generatePayslipPDF(detailPayroll, employee, company, language);
                  }}
                  className="btn btn--primary"
                >
                  <FileText aria-hidden="true" /> {t('Download PDF')}
                </button>
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default Payroll;